import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import config from '../config';

const API_HOST = config.API_HOST;

const PreviewChanges = ({ open, onClose, analysis, accepted }) => {
  const [downloading, setDownloading] = useState(false);
  const [showOriginalDocument, setShowOriginalDocument] = useState(true);

  // Filter only accepted suggestions
  const acceptedSuggestions = analysis.suggestions.filter((_, idx) => accepted[idx]);

  // Get the original text
  const originalText = analysis.originalText;

  // Function to highlight specific sections in the original document
  const getHighlightedOriginalText = () => {
    let text = originalText || '';
    const segments = [];
    let lastIndex = 0;

    // Create sorted array of non-overlapping changes
    const changes = acceptedSuggestions
      .map(sug => ({
        index: text.indexOf(sug.original),
        length: sug.original.length,
        text: sug.original
      }))
      .filter(change => change.index !== -1)
      .sort((a, b) => a.index - b.index);

    // Build segments preserving original whitespace
    changes.forEach(change => {
      if (change.index > lastIndex) {
        // Add unchanged text segment
        segments.push(text.substring(lastIndex, change.index));
      }
      // Add highlighted segment
      segments.push(`<span style="background-color: #ffebe9; color: #cf222e;">${change.text}</span>`);
      lastIndex = change.index + change.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push(text.substring(lastIndex));
    }

    return { __html: segments.join('') };
  };

  // Function to highlight specific sections in the updated document
  const getHighlightedUpdatedText = () => {
    let text = originalText || '';
    const segments = [];
    let lastIndex = 0;

    // Create sorted array of non-overlapping changes
    const changes = acceptedSuggestions
      .map(sug => ({
        index: text.indexOf(sug.original),
        length: sug.original.length,
        original: sug.original,
        suggestion: sug.suggestion
      }))
      .filter(change => change.index !== -1)
      .sort((a, b) => a.index - b.index);

    // Build segments preserving original whitespace
    changes.forEach(change => {
      if (change.index > lastIndex) {
        // Add unchanged text segment
        segments.push(text.substring(lastIndex, change.index));
      }
      // Add highlighted suggestion
      segments.push(`<span style="background-color: #dafbe1; color: #116329;">${change.suggestion}</span>`);
      lastIndex = change.index + change.length;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push(text.substring(lastIndex));
    }

    return { __html: segments.join('') };
  };

  // Function to get updated document content with all accepted changes
  const getUpdatedDocument = () => {
    let updatedText = originalText;

    // Apply all accepted changes
    acceptedSuggestions.forEach(sug => {
      updatedText = updatedText.replace(sug.original, sug.suggestion);
    });

    return updatedText;
  };

  const handleDownloadDOCX = async () => {
    try {
      setDownloading(true);
      const formData = new FormData();
      formData.append('content', getUpdatedDocument());

      const replacements = acceptedSuggestions.map(sug => ({
        original: sug.original,
        suggestion: sug.suggestion
      }));
      formData.append('replacements', JSON.stringify(replacements));

      const response = await fetch(`${API_HOST}/generate-docx`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to generate DOCX');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'updated_resume.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to download DOCX. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Preview Changes</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowOriginalDocument(!showOriginalDocument)}
            sx={{ mb: 2 }}
          >
            {showOriginalDocument ? 'Hide Original Document' : 'Show Original Document'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Original Document Preview */}
          {showOriginalDocument && (
            <Paper
              elevation={3}
              sx={{
                p: 3,
                flex: 1,
                maxHeight: '70vh',
                overflowY: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace'
              }}
            >
              <Typography variant="h6" gutterBottom>Original Document</Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace'
                }}
                dangerouslySetInnerHTML={getHighlightedOriginalText()}
              />
            </Paper>
          )}

          {/* Updated Document Preview */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              flex: 1,
              maxHeight: '70vh',
              overflowY: 'auto'
            }}
          >
            <Typography variant="h6" gutterBottom>Updated Document</Typography>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap'
              }}
              dangerouslySetInnerHTML={getHighlightedUpdatedText()}
            />
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewChanges;
